import { For, Show } from "solid-js";

import type { PearlEndpoint, PearlLineSegment, PearlText } from "./pearl_types";

const LABEL_PX_AT_FIT = 11;
const LINE_PX_AT_FIT = 1.5;
const ENDPOINT_PX_AT_FIT = 2.5;
const LABEL_PADDING_X_PX = 3;
const LABEL_PADDING_Y_PX = 1.5;
const LABEL_CORNER_PX = 2;
const GLYPH_WIDTH_PER_FONT_SIZE = 0.6;
const BASELINE_PER_FONT_SIZE = 0.75;

function nearestEndpoint(ls: PearlLineSegment, point: { x: number; y: number }) {
  const distanceTo = (x: number, y: number) => Math.hypot(point.x - x, point.y - y);
  return distanceTo(ls.x1, ls.y1) <= distanceTo(ls.x2, ls.y2)
    ? { x: ls.x1, y: ls.y1 }
    : { x: ls.x2, y: ls.y2 };
}

export function PearlMeasurementMark(props: {
  lineSegment: PearlLineSegment;
  endpoints: PearlEndpoint[] | null;
  text: PearlText | null;
  color: string;
  imagePxPerScreenPx: number;
}) {
  const px = (screenPx: number) => screenPx * props.imagePxPerScreenPx;

  return (
    <>
      <line
        x1={props.lineSegment.x1}
        y1={props.lineSegment.y1}
        x2={props.lineSegment.x2}
        y2={props.lineSegment.y2}
        stroke={props.color}
        stroke-width={px(LINE_PX_AT_FIT)}
      />

      <For each={props.endpoints ?? []}>
        {(endpoint) => (
          <circle cx={endpoint.x} cy={endpoint.y} r={px(ENDPOINT_PX_AT_FIT)} fill={props.color} />
        )}
      </For>

      <Show when={props.text}>
        {(text) => {
          const fontSize = () => px(LABEL_PX_AT_FIT);
          const textWidth = () => text().text.length * fontSize() * GLYPH_WIDTH_PER_FONT_SIZE;
          const top = () => {
            const anchor = nearestEndpoint(props.lineSegment, text());
            const ourFontPerPearlFont = fontSize() / (text().font_size || 12);
            return {
              x: anchor.x + (text().x - anchor.x) * ourFontPerPearlFont,
              y: anchor.y + (text().y - anchor.y) * ourFontPerPearlFont,
            };
          };

          return (
            <>
              <rect
                x={top().x - px(LABEL_PADDING_X_PX)}
                y={top().y - px(LABEL_PADDING_Y_PX)}
                width={textWidth() + px(LABEL_PADDING_X_PX) * 2}
                height={fontSize() + px(LABEL_PADDING_Y_PX) * 2}
                rx={px(LABEL_CORNER_PX)}
                ry={px(LABEL_CORNER_PX)}
                fill="rgba(50, 50, 50, 0.85)"
              />
              <text
                x={top().x}
                y={top().y + fontSize() * BASELINE_PER_FONT_SIZE}
                fill={props.color}
                font-size={`${fontSize()}px`}
                font-family="sans-serif"
                font-weight="500"
              >
                {text().text}
              </text>
            </>
          );
        }}
      </Show>
    </>
  );
}
